import React, { useState, useEffect } from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import axios from 'axios';
import Login from './pages/Login';
import ForgotPassword from './pages/ForgotPassword';
import ResetPassword from './pages/ResetPassword';
import AdminDashboard from './pages/AdminDashboard';
import CustomerHome from './pages/CustomerHome';
import ResidentialHome from './pages/ResidentialHome';
import TechnicianApp from './pages/TechnicianApp';
import './App.css';

function App() {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    axios.get('/api/auth/me')
      .then(res => setUser(res.data))
      .catch(() => setUser(null))
      .finally(() => setLoading(false));
  }, []);

  if (loading) {
    return <div className="loading">טוען...</div>;
  }

  // Phase 5: residential customers get the PAL-style minimal shell at
  // /r/*; everyone else (industrial customers, staff) stays on the
  // existing routes. The residential branch is short-circuited at
  // the App level rather than being a sibling Route inside the main
  // <Routes>, because React Router 7's path matching can fall through
  // a `/*` catch-all even when a more specific `/r/*` sibling exists.
  const isResidentialCustomer =
    user && user.role !== 'super_admin' && user.role !== 'technician'
      && user.customer_type === 'residential';

  if (isResidentialCustomer) {
    return (
      <BrowserRouter>
        <Routes>
          <Route path="/forgot-password" element={<ForgotPassword />} />
          <Route path="/reset-password" element={<ResetPassword />} />
          <Route
            path="/login"
            element={<Navigate to="/r/" replace />}
          />
          <Route
            path="/r/*"
            element={<ResidentialHome user={user} setUser={setUser} />}
          />
          <Route path="*" element={<Navigate to="/r/" replace />} />
        </Routes>
      </BrowserRouter>
    );
  }

  // Super-admin "preview" hatch: any super_admin can hit /r/preview/*
  // to see the residential UI shell (with mocked gates) without being
  // a real residential customer. Lets Huri staff QA the look before
  // any residential customer exists in the DB. Real residential
  // customers never go through this — they short-circuit above.
  const isSuperAdminPreview =
    user && user.role === 'super_admin' &&
    window.location.pathname.startsWith('/r/preview');

  if (isSuperAdminPreview) {
    return (
      <BrowserRouter>
        <Routes>
          <Route
            path="/r/preview/*"
            element={
              <ResidentialHome
                user={user}
                setUser={setUser}
                basePath="/r/preview"
              />
            }
          />
          <Route path="*" element={<Navigate to="/r/preview/" replace />} />
        </Routes>
      </BrowserRouter>
    );
  }

  return (
    <BrowserRouter>
      <Routes>
        <Route
          path="/login"
          element={user ? <Navigate to="/" /> : <Login onLogin={setUser} />}
        />
        <Route path="/forgot-password" element={<ForgotPassword />} />
        <Route path="/reset-password" element={<ResetPassword />} />
        <Route
          path="/*"
          element={
            !user ? <Navigate to="/login" /> :
            user.role === 'super_admin'
              ? <AdminDashboard user={user} setUser={setUser} />
            : user.role === 'technician'
              ? <TechnicianApp user={user} setUser={setUser} />
            : <CustomerHome user={user} setUser={setUser} />
          }
        />
      </Routes>
    </BrowserRouter>
  );
}

export default App;
